import { useEffect, useRef, useState, type FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d96clinpg5pqhofcuneg';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const FINISHES: Array<{ name: string; hex: string }> = [
  { name: 'Reference Onyx', hex: '#1A1D24' },
  { name: 'Slate Green', hex: '#5A8A78' },
  { name: 'Warm Sand', hex: '#C3A97E' },
];

const METRICS = [
  { label: 'DEPOSIT', value: '€ 240' },
  { label: 'BUILD', value: '11 days' },
  { label: 'WARRANTY', value: '5 years' },
  { label: 'SHIPS', value: 'Q3 2026' },
];

export default function ReserveSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [selectedFinish, setSelectedFinish] = useState<string>(FINISHES[0].name);
  const [msgLen, setMsgLen] = useState<number>(0);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setInView(true);
        });
      },
      { threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const fadeUp = (delay: number, distance = 18) => ({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0)' : `translateY(${distance}px)`,
    transition: `opacity 900ms ease ${delay}ms, transform 900ms cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const formEl = event.currentTarget;
    const rawData = new FormData(formEl);

    const trap = String(rawData.get('company_alt') ?? '').trim();
    if (trap.length > 0) {
      setStatus('success');
      return;
    }
    rawData.delete('company_alt');

    const params = new URLSearchParams();
    rawData.forEach((val, key) => {
      params.append(key, String(val));
    });

    setStatus('submitting');
    setErrorMsg('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverCode = parsed?.code;
      const serverMsg =
        parsed?.meta?.message ??
        parsed?.message ??
        parsed?.meta?.detail ??
        '';

      const looksLikeSpam =
        typeof serverMsg === 'string' && /spam/i.test(serverMsg);
      const looksLikeSpamInBody =
        typeof responseText === 'string' && /spam/i.test(responseText);

      if (
        response.ok &&
        serverCode === 'OK' &&
        !looksLikeSpam &&
        !looksLikeSpamInBody
      ) {
        setStatus('success');
        formEl.reset();
        setSelectedFinish(FINISHES[0].name);
        setMsgLen(0);
      } else {
        setStatus('error');
        setErrorMsg(
          (typeof serverMsg === 'string' && serverMsg) ||
            'Something went wrong. Please try again in a moment.'
        );
      }
    } catch {
      setStatus('error');
      setErrorMsg(
        'We could not reach the reservation service. Please check your connection and try again.'
      );
    }
  };

  return (
    <section
      ref={sectionRef}
      id="reserve"
      className="relative w-full"
      style={{
        backgroundColor: '#141B2A',
        paddingTop: '110px',
        paddingBottom: '90px',
        fontFamily:
          'Inter, "Helvetica Neue", Helvetica, Arial, system-ui, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        className="mx-auto"
        style={{ maxWidth: '1280px', paddingLeft: '56px', paddingRight: '56px' }}
      >
        {/* Editorial header */}
        <div className="grid grid-cols-12 gap-8 items-end mb-14">
          <div className="col-span-12 lg:col-span-7">
            <div
              className="uppercase"
              style={{
                color: '#7AA8EE',
                fontSize: '11px',
                letterSpacing: '0.32em',
                fontWeight: 500,
                marginBottom: '24px',
                ...fadeUp(80, 10),
              }}
            >
              007 — RESERVE
            </div>
            <h2
              style={{
                color: '#FFFFFF',
                fontSize: '104px',
                fontWeight: 700,
                lineHeight: 0.9,
                letterSpacing: '-0.045em',
                margin: 0,
                ...fadeUp(180, 22),
              }}
            >
              First ships,
              <br />
              <span
                style={{
                  fontStyle: 'italic',
                  fontWeight: 500,
                  color: 'rgba(255,255,255,0.72)',
                }}
              >
                Q3 · 2026.
              </span>
            </h2>
          </div>

          <div className="col-span-12 lg:col-span-5 lg:pb-4">
            <p
              style={{
                color: '#B7BCC7',
                fontSize: '16px',
                lineHeight: 1.8,
                margin: 0,
                ...fadeUp(280, 14),
              }}
            >
              Refundable deposit. Finish confirmed before assembly.
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="grid grid-cols-12 gap-14 items-start">
          {/* Metrics rail */}
          <div className="col-span-12 lg:col-span-4">
            <div
              className="uppercase"
              style={{
                color: 'rgba(255,255,255,0.42)',
                fontSize: '10px',
                letterSpacing: '0.32em',
                fontWeight: 500,
                marginBottom: '18px',
                paddingBottom: '18px',
                borderBottom: '1px solid rgba(255,255,255,0.12)',
                ...fadeUp(360, 14),
              }}
            >
              DETAILS
            </div>

            <div className="flex flex-col" style={fadeUp(440, 14)}>
              {METRICS.map((m, idx) => (
                <div
                  key={m.label}
                  className="grid grid-cols-[1fr_auto] gap-4 items-baseline group cursor-default"
                  style={{
                    paddingTop: '20px',
                    paddingBottom: '20px',
                    borderBottom:
                      idx < METRICS.length - 1
                        ? '1px solid rgba(255,255,255,0.06)'
                        : undefined,
                  }}
                >
                  <div
                    className="uppercase"
                    style={{
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: '10px',
                      letterSpacing: '0.28em',
                      fontWeight: 500,
                    }}
                  >
                    {m.label}
                  </div>
                  <div
                    style={{
                      color: '#FFFFFF',
                      fontSize: '22px',
                      fontWeight: 700,
                      letterSpacing: '-0.005em',
                      fontFeatureSettings: '"tnum"',
                      transition: 'transform 400ms cubic-bezier(0.22,1,0.36,1), color 260ms ease',
                    }}
                    className="group-hover:translate-x-[-4px] group-hover:text-[#7AA8EE]"
                  >
                    {m.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Studio quote */}
            <div
              className="mt-14"
              style={{
                borderLeft: '1px solid rgba(122,168,238,0.55)',
                paddingLeft: '20px',
                ...fadeUp(560, 14),
              }}
            >
              <p
                style={{
                  color: '#D0D4DC',
                  fontSize: '17px',
                  fontWeight: 500,
                  lineHeight: 1.6,
                  fontStyle: 'italic',
                  margin: 0,
                  marginBottom: '10px',
                }}
              >
                "Signed. Boxed by hand."
              </p>
              <div
                className="uppercase"
                style={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '10px',
                  letterSpacing: '0.32em',
                  fontWeight: 500,
                }}
              >
                — STUDIO 07
              </div>
            </div>
          </div>

          {/* Form column */}
          <div className="col-span-12 lg:col-span-8" style={fadeUp(500, 18)}>
            {status === 'success' ? (
              <div
                className="flex flex-col items-start axiom-fade-swap"
                style={{
                  minHeight: '460px',
                  justifyContent: 'center',
                  paddingLeft: '4px',
                }}
              >
                <div
                  className="flex items-center justify-center mb-8"
                  style={{
                    width: '54px',
                    height: '54px',
                    borderRadius: '50%',
                    border: '1px solid rgba(122,168,238,0.4)',
                    background: 'rgba(122,168,238,0.08)',
                  }}
                >
                  <i
                    className="ri-check-line"
                    style={{ color: '#7AA8EE', fontSize: '24px' }}
                  />
                </div>
                <div
                  className="uppercase"
                  style={{
                    color: '#7AA8EE',
                    fontSize: '11px',
                    letterSpacing: '0.32em',
                    fontWeight: 500,
                    marginBottom: '16px',
                  }}
                >
                  RESERVED
                </div>
                <h3
                  style={{
                    color: '#FFFFFF',
                    fontSize: '40px',
                    fontWeight: 700,
                    lineHeight: 1.05,
                    letterSpacing: '-0.02em',
                    margin: 0,
                    marginBottom: '16px',
                  }}
                >
                  We&apos;ll be in touch soon.
                </h3>
                <p
                  style={{
                    color: '#B7BCC7',
                    fontSize: '16px',
                    lineHeight: 1.75,
                    margin: 0,
                    maxWidth: '460px',
                  }}
                >
                  Confirmation sent. Studio 07 will follow up within two days.
                </p>
              </div>
            ) : (
              <form
                id="axiom-reserve-form"
                data-readdy-form
                onSubmit={handleSubmit}
                noValidate
              >
                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2 mb-2">
                  <UnderlineField
                    label="Full name"
                    name="fullName"
                    type="text"
                    autoComplete="name"
                    placeholder="Ari Tanaka"
                    required
                  />
                  <UnderlineField
                    label="Email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="ari@studio.co"
                    required
                  />
                </div>

                {/* Country + Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2 mb-8">
                  <UnderlineField
                    label="Country"
                    name="country"
                    type="text"
                    autoComplete="country"
                    placeholder="Japan"
                    required
                  />
                  <UnderlineField
                    label="Phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="+81 90 0000 0000"
                  />
                </div>

                {/* Finish picker */}
                <div className="mb-8">
                  <FieldLabel>Preferred finish</FieldLabel>
                  <div
                    className="grid grid-cols-3 gap-0"
                    style={{
                      borderTop: '1px solid rgba(255,255,255,0.14)',
                      borderBottom: '1px solid rgba(255,255,255,0.14)',
                    }}
                  >
                    {FINISHES.map((f, idx) => {
                      const isActive = f.name === selectedFinish;
                      return (
                        <button
                          key={f.name}
                          type="button"
                          onClick={() => setSelectedFinish(f.name)}
                          className="relative whitespace-nowrap cursor-pointer text-left flex items-center gap-3"
                          style={{
                            padding: '18px 12px',
                            background: 'transparent',
                            border: 'none',
                            borderRight:
                              idx < FINISHES.length - 1
                                ? '1px solid rgba(255,255,255,0.08)'
                                : undefined,
                            color: isActive
                              ? '#FFFFFF'
                              : 'rgba(255,255,255,0.55)',
                            fontSize: '15px',
                            fontWeight: isActive ? 600 : 500,
                            letterSpacing: '0.005em',
                            transition: 'color 220ms ease',
                          }}
                        >
                          <span
                            className="absolute left-0 right-0 top-0"
                            style={{
                              height: '2px',
                              background: '#7AA8EE',
                              transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
                              transformOrigin: 'left center',
                              transition:
                                'transform 420ms cubic-bezier(0.22,1,0.36,1)',
                            }}
                          />
                          <span
                            className="inline-block flex-shrink-0"
                            style={{
                              width: '18px',
                              height: '18px',
                              borderRadius: '50%',
                              backgroundColor: f.hex,
                              border: `1px solid ${
                                isActive
                                  ? 'rgba(122,168,238,0.55)'
                                  : 'rgba(255,255,255,0.18)'
                              }`,
                              boxShadow: isActive
                                ? '0 0 0 3px rgba(122,168,238,0.14)'
                                : 'none',
                              transition:
                                'border-color 220ms ease, box-shadow 320ms ease',
                            }}
                          />
                          <span>{f.name}</span>
                        </button>
                      );
                    })}
                  </div>
                  <input type="hidden" name="finish" value={selectedFinish} />
                </div>

                {/* Notes */}
                <div className="mb-10 axiom-underline-field">
                  <div className="flex items-baseline justify-between mb-2">
                    <FieldLabel>Notes for the studio</FieldLabel>
                    <span
                      style={{
                        color:
                          msgLen > 450
                            ? '#7AA8EE'
                            : 'rgba(255,255,255,0.35)',
                        fontSize: '10px',
                        letterSpacing: '0.06em',
                        fontFeatureSettings: '"tnum"',
                        transition: 'color 220ms ease',
                      }}
                    >
                      {msgLen} / 500
                    </span>
                  </div>
                  <textarea
                    name="notes"
                    maxLength={500}
                    rows={3}
                    onChange={(e) => setMsgLen(e.target.value.length)}
                    placeholder="Engraving, delivery, or a hello."
                    className="w-full outline-none resize-none"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid rgba(255,255,255,0.14)',
                      padding: '10px 0 14px 0',
                      color: '#FFFFFF',
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: 1.55,
                      letterSpacing: '0.005em',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                  aria-hidden="true"
                  className="axiom-honeypot"
                />

                {status === 'error' && errorMsg && (
                  <div
                    className="mb-6"
                    style={{
                      color: '#E88B7A',
                      fontSize: '13.5px',
                      lineHeight: 1.55,
                      padding: '12px 16px',
                      borderRadius: '6px',
                      background: 'rgba(232, 139, 122, 0.08)',
                      border: '1px solid rgba(232, 139, 122, 0.28)',
                    }}
                  >
                    {errorMsg}
                  </div>
                )}

                {/* Submit */}
                <div className="flex items-center gap-6 flex-wrap">
                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="group relative overflow-hidden inline-flex items-center gap-3 whitespace-nowrap cursor-pointer axiom-reserve-cta"
                    style={{
                      padding: '20px 34px',
                      borderRadius: '999px',
                      border: 'none',
                      background:
                        status === 'submitting'
                          ? 'rgba(122,168,238,0.4)'
                          : '#7AA8EE',
                      color: '#0C1017',
                      fontSize: '12px',
                      fontWeight: 700,
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      transition:
                        'background 240ms ease, opacity 240ms ease, transform 240ms ease, box-shadow 320ms ease',
                      opacity: status === 'submitting' ? 0.85 : 1,
                    }}
                  >
                    {/* animated fill sweep on hover */}
                    <span
                      className="absolute inset-0 axiom-cta-sweep"
                      aria-hidden="true"
                    />
                    <span className="relative z-10 inline-flex items-center gap-1">
                      {status === 'submitting' ? (
                        <>
                          <span>Submitting</span>
                          <span className="axiom-dots">
                            <span>.</span>
                            <span>.</span>
                            <span>.</span>
                          </span>
                        </>
                      ) : (
                        'Confirm reservation'
                      )}
                    </span>
                    <i
                      className="ri-arrow-right-line relative z-10 transition-transform duration-300 group-hover:translate-x-1.5"
                      style={{ fontSize: '15px' }}
                    />
                  </button>

                  <span
                    style={{
                      color: 'rgba(255,255,255,0.42)',
                      fontSize: '12px',
                      lineHeight: 1.6,
                      maxWidth: '320px',
                      letterSpacing: '0.005em',
                    }}
                  >
                    Refundable up to 7 days before assembly.
                  </span>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label
      className="flex items-center uppercase axiom-underline-label"
      style={{
        color: 'rgba(255,255,255,0.5)',
        fontSize: '10px',
        letterSpacing: '0.32em',
        fontWeight: 500,
        marginBottom: '10px',
      }}
    >
      {children}
    </label>
  );
}

type UnderlineFieldProps = {
  label: string;
  name: string;
  type?: string;
  autoComplete?: string;
  placeholder?: string;
  required?: boolean;
};

function UnderlineField({
  label,
  name,
  type = 'text',
  autoComplete,
  placeholder,
  required,
}: UnderlineFieldProps) {
  return (
    <div className="flex flex-col mb-6 axiom-underline-field">
      <FieldLabel>{label}</FieldLabel>
      <input
        name={name}
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        required={required}
        className="w-full outline-none"
        style={{
          background: 'transparent',
          border: 'none',
          borderBottom: '1px solid rgba(255,255,255,0.14)',
          padding: '10px 0 14px 0',
          color: '#FFFFFF',
          fontSize: '17px',
          fontWeight: 500,
          letterSpacing: '0.005em',
          fontFamily: 'inherit',
        }}
      />
    </div>
  );
}